/* global __APP_VERSION__ */
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  Target,
  BarChart3,
  GitCompare,
  Activity,
  Radar,
  Calculator,
  Wallet,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Banknote,
  Megaphone,
  Store,
  Package,
  ExternalLink,
  Link2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import SaltyLogo from '../ui/SaltyLogo';

// Main navigation - internal routes
const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'AOP Planning', href: '/aop', icon: Target },
  { name: 'Actuals', href: '/actuals', icon: BarChart3 },
  { name: 'Category Mapping', href: '/category-mapping', icon: Link2 },
  { name: 'Variance', href: '/variance', icon: GitCompare },
  { name: 'Working Capital', href: '/working-capital', icon: Wallet },
  { name: 'Channels', href: '/channels', icon: Activity },
  { name: 'Health Radar', href: '/health', icon: Radar },
  { name: 'Simulator', href: '/simulator', icon: Calculator },
];

// External SALTY Tools
const externalTools = [
  { name: 'Finance', href: 'https://finance.salty.co.in/', icon: Banknote },
  { name: 'Marketing', href: 'https://marketing.salty.co.in/', icon: Megaphone },
  { name: 'B2B Module', href: 'https://merchant.salty.co.in/', icon: Store },
  { name: 'Procurement', href: 'https://saltymerchant.co/', icon: Package },
];

const bottomNav = [
  { name: 'Settings', href: '/settings', icon: Settings },
];

export const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const { logout, user } = useAuth();

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-white border-r border-slate-200 sidebar-transition flex flex-col',
        collapsed ? 'w-[60px]' : 'w-[220px]'
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="h-12 flex items-center justify-between px-3 border-b border-slate-100">
        {!collapsed && <SaltyLogo height={36} variant="orange" showWordmark={true} />}
        {collapsed && (
          <img src="/favicon-cat-orange.png" alt="Salty" style={{ height: 32, width: 'auto', margin: '0 auto' }} />
        )}
        <button
          onClick={onToggle}
          className={cn(
            'p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors',
            collapsed && 'mx-auto mt-2'
          )}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={cn(
                'sidebar-link',
                isActive && 'sidebar-link-active'
              )}
              data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className={cn('w-5 h-5 flex-shrink-0', isActive ? 'text-primary-700' : 'text-slate-400')} />
              {!collapsed && <span>{item.name}</span>}
            </NavLink>
          );
        })}

        {/* SALTY Tools Section */}
        <div className="pt-2 mt-2 border-t border-slate-200">
          {!collapsed && (
            <p className="px-3 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Business Functions
            </p>
          )}
          {collapsed && (
            <div className="flex justify-center mb-2">
              <div className="w-6 h-px bg-slate-300" />
            </div>
          )}
          {externalTools.map((tool) => (
            <a
              key={tool.name}
              href={tool.href}
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-link group"
              data-testid={`external-${tool.name.toLowerCase().replace(' ', '-')}`}
            >
              <tool.icon className="w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-primary-600" />
              {!collapsed && (
                <>
                  <span className="flex-1">{tool.name}</span>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-300 group-hover:text-primary-500" />
                </>
              )}
            </a>
          ))}
        </div>
      </nav>

      {/* Bottom section */}
      <div className="px-2 py-2 border-t border-slate-100 space-y-0.5">
        {bottomNav.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={cn(
                'sidebar-link',
                isActive && 'sidebar-link-active'
              )}
              data-testid={`nav-${item.name.toLowerCase()}`}
            >
              <item.icon className={cn('w-5 h-5 flex-shrink-0', isActive ? 'text-primary-700' : 'text-slate-400')} />
              {!collapsed && <span>{item.name}</span>}
            </NavLink>
          );
        })}
        
        <button
          onClick={logout}
          className="sidebar-link w-full text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>

        {/* User info */}
        {!collapsed && user && (
          <div className="mt-4 p-3 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium text-slate-900 truncate">{user.name}</p>
            <p className="text-xs text-slate-500 truncate">{user.email}</p>
          </div>
        )}

        {/* System status + app version — version is injected from
            package.json at build time via webpack DefinePlugin,
            see craco.config.js */}
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className={cn(
            'flex items-center border-t border-slate-100 mt-3 pt-3',
            collapsed ? 'justify-center' : 'justify-between px-1'
          )}
          title={`Salty Control Tower v${__APP_VERSION__} — operational`}
        >
          <div className="flex items-center gap-1.5">
            <span className="relative flex h-1.5 w-1.5 shrink-0">
              <motion.span
                className="absolute inline-flex h-full w-full rounded-full bg-emerald-400"
                animate={{ scale: [1, 2.4], opacity: [0.6, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
              />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            {!collapsed && (
              <span className="text-[10px] font-medium text-slate-400 select-none">
                Operational
              </span>
            )}
          </div>

          {!collapsed && (
            <span
              className="font-mono text-[10px] tracking-wide text-slate-300 select-none"
              data-testid="app-version"
            >
              v{__APP_VERSION__}
            </span>
          )}
        </motion.div>
      </div>
    </aside>
  );
};

export default Sidebar;
